import { FC, ReactNode, useState } from 'react';
import { Button } from 'antd';
import { TbChevronRight } from 'react-icons/tb';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';

import MiddleDot from '../typography/MiddleDot';
import { CompanySearchResult, CompanySearchResultGroup, summarizeCompanyListings } from './utils';

interface CompanyResultGroupRowsProps {
    group: CompanySearchResultGroup;
    renderResult: (result: CompanySearchResult) => ReactNode;
}

const CompanyResultGroupRows: FC<CompanyResultGroupRowsProps> = ({ group, renderResult }): JSX.Element => {
    const [expanded, setExpanded] = useState(false);
    const { token } = useAntDesignTheme();
    const listingSummary = summarizeCompanyListings([group.primary, ...group.alternatives]);
    const summaryItems = [
        listingSummary.equities
            ? `${listingSummary.equities.toLocaleString()} ${listingSummary.equities === 1 ? 'Company' : 'Companies'}`
            : undefined,
        listingSummary.funds
            ? `${listingSummary.funds.toLocaleString()} ${listingSummary.funds === 1 ? 'Fund' : 'Funds'}`
            : undefined,
        listingSummary.indices
            ? `${listingSummary.indices.toLocaleString()} ${listingSummary.indices === 1 ? 'Index' : 'Indices'}`
            : undefined,
    ].filter((item): item is string => Boolean(item));

    return (
        <>
            {renderResult(group.primary)}
            {group.alternatives.length > 0 ? (
                <div
                    style={{
                        borderBottom: `1px solid ${token.colorBorder}`,
                        padding: '0.25rem 0',
                    }}
                >
                    <Button
                        onClick={() => setExpanded((value) => !value)}
                        size='small'
                        style={{ color: token.colorTextTertiary, marginLeft: '0.25rem' }}
                        type='text'
                    >
                        <TbChevronRight
                            style={{
                                transform: expanded ? 'rotate(90deg)' : undefined,
                                transition: 'transform 160ms ease',
                                verticalAlign: '-0.125rem',
                            }}
                        />
                        {expanded ? 'Hide' : 'Show'} {group.alternatives.length} other{' '}
                        {group.alternatives.length === 1 ? 'listing' : 'listings'}
                    </Button>
                </div>
            ) : null}
            {expanded ? group.alternatives.map(renderResult) : null}
            {expanded && group.alternatives.length > 0 ? (
                <div
                    style={{
                        borderBottom: `1px solid ${token.colorBorder}`,
                        color: token.colorTextTertiary,
                        minHeight: '1.75rem',
                        padding: '0.25rem 0.75rem',
                    }}
                >
                    <small>
                        {listingSummary.total.toLocaleString()} listings total
                        {summaryItems.map((item) => (
                            <span key={item}>
                                <MiddleDot horizontalMargin='0.375rem' />
                                {item}
                            </span>
                        ))}
                    </small>
                </div>
            ) : null}
        </>
    );
};

export default CompanyResultGroupRows;
